import React, { useState, useRef, useEffect } from 'react'

import cx from 'classnames'
import ResizeObserver from 'resize-observer-polyfill'

import styles from './AnimatedCollapse.module.scss'

type CollapsibleChildProps = {
  readonly ref: React.MutableRefObject<Element | null>
}

export type CollapsibleElement<T extends CollapsibleChildProps = CollapsibleChildProps> = React.ReactElement<
  T,
  string | React.JSXElementConstructor<T>
>

const collapsedStyle = {
  height: 0,
  padding: 0,
}

export const AnimatedCollapse = <T extends CollapsibleElement>({
  collapse,
  children,
  className: additionalClasses,
}: {
  collapse?: boolean
  className?: string | string[]
  children: T
}) => {
  const [style, setStyle] = useState<Record<string, unknown>>({})
  const contentRef = useRef<HTMLDivElement | null>(null)
  const childRef = useRef<Element | null>(null)
  useEffect(() => {
    const el = childRef.current
    if (!el) return
    const collapseFn = (entries: ResizeObserverEntry[]) => {
      const height = entries[0].target.getBoundingClientRect().height
      setStyle({ height })
    }
    const resizeObserver = new ResizeObserver((entries) => {
      window.requestAnimationFrame(() => collapseFn(entries))
    })
    resizeObserver.observe(el)
    return () => {
      resizeObserver.disconnect()
    }
  }, [children])

  if (React.Children.count(children) > 1) {
    throw new Error('too many children')
  }

  return (
    <div style={collapse ? collapsedStyle : style} ref={contentRef} className={cx(styles.Content, additionalClasses)}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, { ref: childRef })
        }
      })}
    </div>
  )
}
